import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronRight, Sparkles } from "lucide-react";
import { useGetMe } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface ReleaseNote {
  id: number;
  date: string;
  title: string;
  description?: string;
  timestamp?: string;
}

const LAST_SEEN_KEY = "tex_whats_new_last_seen";
const THIRTY_MIN = 30 * 60 * 1000;

type GroupItem =
  | { kind: "note"; note: ReleaseNote }
  | { kind: "burst"; key: number; notes: ReleaseNote[]; start: Date; end: Date };

interface DateGroup {
  date: string;
  label: string;
  count: number;
  items: GroupItem[];
}

function parseTimestamp(ts?: string): number | null {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  return Number.isNaN(t) ? null : t;
}

function relativeLabel(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  const target = new Date(parsed);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (today.getTime() - target.getTime()) / 86_400_000,
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTimeRange(start: Date, end: Date): string {
  const s = start.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const e = end.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (s === e) return s;
  const [sTime, sMer] = s.split(" ");
  const eMer = e.split(" ")[1];
  if (sMer && eMer && sMer === eMer) return `${sTime}\u2013${e}`;
  return `${s}\u2013${e}`;
}

function buildItems(notes: ReleaseNote[]): GroupItem[] {
  const items: GroupItem[] = [];
  let i = 0;
  while (i < notes.length) {
    const ts = parseTimestamp(notes[i].timestamp);
    if (ts === null) {
      items.push({ kind: "note", note: notes[i] });
      i += 1;
      continue;
    }
    const run = [notes[i]];
    let runMin = ts;
    let runMax = ts;
    let j = i + 1;
    while (j < notes.length) {
      const next = parseTimestamp(notes[j].timestamp);
      if (next === null) break;
      const nextMin = Math.min(runMin, next);
      const nextMax = Math.max(runMax, next);
      if (nextMax - nextMin > THIRTY_MIN) break;
      run.push(notes[j]);
      runMin = nextMin;
      runMax = nextMax;
      j += 1;
    }
    if (run.length >= 3) {
      items.push({
        kind: "burst",
        key: run[0].id,
        notes: run,
        start: new Date(runMin),
        end: new Date(runMax),
      });
      i = j;
    } else {
      items.push({ kind: "note", note: notes[i] });
      i += 1;
    }
  }
  return items;
}

function groupNotes(notes: ReleaseNote[]): DateGroup[] {
  const byDate: { date: string; notes: ReleaseNote[] }[] = [];
  for (const note of notes) {
    const last = byDate[byDate.length - 1];
    if (last && last.date === note.date) last.notes.push(note);
    else byDate.push({ date: note.date, notes: [note] });
  }
  return byDate.map(({ date, notes: dateNotes }) => ({
    date,
    label: relativeLabel(date),
    count: dateNotes.length,
    items: buildItems(dateNotes),
  }));
}

function itemKey(item: GroupItem): string {
  return item.kind === "note" ? `note-${item.note.id}` : `burst-${item.key}`;
}

function itemNewestTs(item: GroupItem): number | null {
  if (item.kind === "burst") return item.end.getTime();
  return parseTimestamp(item.note.timestamp);
}

// The divider sits above the first item (newest-first) that predates the user's
// previous login. Only show it when the boundary is interior: at least one item
// above is new and at least one below is old. Returns the key of the item the
// divider should be rendered above, or null for no marker.
function computeDividerKey(
  groups: DateGroup[],
  prevLoginMs: number | null,
): string | null {
  if (prevLoginMs === null) return null;
  const flat = groups.flatMap((g) => g.items);
  let firstOldIndex = -1;
  for (let i = 0; i < flat.length; i += 1) {
    const ts = itemNewestTs(flat[i]);
    const isOld = ts === null ? true : ts <= prevLoginMs;
    if (isOld) {
      firstOldIndex = i;
      break;
    }
  }
  if (firstOldIndex <= 0) return null;
  return itemKey(flat[firstOldIndex]);
}

function LastVisitDivider() {
  return (
    <div
      role="separator"
      className="flex items-center gap-2 py-2 first:pt-0"
    >
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Since you were last here
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function WhatsNewButton() {
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [lastSeen, setLastSeen] = useState<number>(() => {
    const stored =
      typeof window !== "undefined"
        ? window.localStorage.getItem(LAST_SEEN_KEY)
        : null;
    const parsed = stored ? Number(stored) : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  });
  const [open, setOpen] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [expandedBursts, setExpandedBursts] = useState<Set<number>>(new Set());

  useEffect(() => {
    const url = `${import.meta.env.BASE_URL}release-notes.json`;
    let cancelled = false;
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: ReleaseNote[]) => {
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => b.id - a.id);
        setNotes(sorted);
      })
      .catch(() => {
        if (!cancelled) setNotes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: me } = useGetMe();

  const groups = useMemo(() => groupNotes(notes), [notes]);

  const prevLoginMs = useMemo(
    () => parseTimestamp(me?.previousLoginAt ?? undefined),
    [me?.previousLoginAt],
  );
  const dividerKey = useMemo(
    () => computeDividerKey(groups, prevLoginMs),
    [groups, prevLoginMs],
  );

  const maxId = notes.length > 0 ? notes[0].id : 0;
  const unreadCount = notes.filter((n) => n.id > lastSeen).length;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      const defaultBursts = new Set<number>();
      for (const group of groups) {
        for (const item of group.items) {
          if (item.kind === "burst" && item.notes.some((n) => n.id > lastSeen)) {
            defaultBursts.add(item.key);
          }
        }
      }
      setExpandedBursts(defaultBursts);
      setExpandedNotes(new Set());
      if (maxId > lastSeen) {
        window.localStorage.setItem(LAST_SEEN_KEY, String(maxId));
        setLastSeen(maxId);
      }
    }
  }

  function toggleNote(id: number) {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleBurst(key: number) {
    setExpandedBursts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function renderNote(note: ReleaseNote) {
    const description = note.description?.trim() ?? "";
    const hasDescription = description.length > 0;
    const expanded = expandedNotes.has(note.id);
    const unread = note.id > lastSeen;
    const time = note.timestamp ? formatTime(note.timestamp) : "";
    return (
      <div key={note.id} className="py-1">
        <button
          type="button"
          onClick={() => hasDescription && toggleNote(note.id)}
          aria-expanded={hasDescription ? expanded : undefined}
          className={cn(
            "flex w-full items-start gap-2 text-left",
            hasDescription ? "cursor-pointer" : "cursor-default",
          )}
        >
          <span className="mt-[7px] flex h-1.5 w-1.5 shrink-0 items-center justify-center">
            {unread && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            )}
          </span>
          <ChevronRight
            className={cn(
              "mt-[3px] h-3 w-3 shrink-0 text-muted-foreground transition-transform",
              expanded && "rotate-90",
              !hasDescription && "invisible",
            )}
          />
          <span className="flex-1 text-sm leading-snug text-foreground">
            {note.title}
          </span>
          {time && (
            <span className="mt-[1px] shrink-0 text-[10px] tabular-nums text-muted-foreground">
              {time}
            </span>
          )}
        </button>
        {expanded && hasDescription && (
          <p className="mt-1 pl-7 text-xs leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    );
  }

  function renderBurst(item: Extract<GroupItem, { kind: "burst" }>) {
    const expanded = expandedBursts.has(item.key);
    const hasUnread = item.notes.some((n) => n.id > lastSeen);
    return (
      <div key={`burst-${item.key}`} className="py-1">
        <button
          type="button"
          onClick={() => toggleBurst(item.key)}
          aria-expanded={expanded}
          className="flex w-full items-center gap-2 text-left"
        >
          <span className="flex h-1.5 w-1.5 shrink-0 items-center justify-center">
            {hasUnread && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            )}
          </span>
          <ChevronRight
            className={cn(
              "h-3 w-3 shrink-0 text-muted-foreground transition-transform",
              expanded && "rotate-90",
            )}
          />
          <span className="flex-1 text-xs font-medium text-muted-foreground">
            {formatTimeRange(item.start, item.end)} &middot;{" "}
            {item.notes.length} updates
          </span>
        </button>
        {expanded && (
          <div className="ml-[3px] mt-1 flex flex-col border-l border-border pl-3">
            {item.notes.map(renderNote)}
          </div>
        )}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        aria-label="What's New"
        className="relative h-7 inline-flex items-center gap-1.5 rounded-md border border-input bg-muted/50 px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Sparkles className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">What's New</span>
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground">
            {unreadCount}
          </span>
        )}
      </button>

      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            What's New
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-5">
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No updates to show yet.
            </p>
          ) : (
            groups.map((group) => {
              const firstKey = group.items[0]
                ? itemKey(group.items[0])
                : null;
              const dividerAtGroupTop =
                dividerKey !== null && dividerKey === firstKey;
              return (
                <Fragment key={group.date}>
                  {dividerAtGroupTop && <LastVisitDivider />}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground">
                        {group.label}
                      </span>
                      <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-medium leading-none text-muted-foreground">
                        {group.count}
                      </span>
                    </div>
                    <div className="flex flex-col divide-y divide-border/60">
                      {group.items.map((item) => {
                        const key = itemKey(item);
                        const showDivider =
                          dividerKey === key && !dividerAtGroupTop;
                        return (
                          <Fragment key={key}>
                            {showDivider && <LastVisitDivider />}
                            {item.kind === "note"
                              ? renderNote(item.note)
                              : renderBurst(item)}
                          </Fragment>
                        );
                      })}
                    </div>
                  </div>
                </Fragment>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
